/**
 * Offer bundle personalization sync — eligibility helpers (label-only v1).
 * Pure functions for Node tests + browser (window.PBOfferBundleSync).
 */
(function (global) {
  'use strict';

  function normalizeBundleFieldLabel(label) {
    return String(label == null ? '' : label).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function getPersonalizationTemplateLabelSet(item) {
    const set = new Set();
    if (!item || item.skipPersonalizationInBundle) return set;
    (item.config || []).forEach(function (field) {
      if (field && field.label) set.add(normalizeBundleFieldLabel(field.label));
    });
    return set;
  }

  function getPersonalizationLabelOverlap(sourceItem, targetItem) {
    const overlap = new Set();
    if (!sourceItem || !targetItem) return overlap;
    if (sourceItem.skipPersonalizationInBundle || targetItem.skipPersonalizationInBundle) return overlap;
    const srcLabels = getPersonalizationTemplateLabelSet(sourceItem);
    const tgtLabels = getPersonalizationTemplateLabelSet(targetItem);
    tgtLabels.forEach(function (label) {
      if (srcLabels.has(label)) overlap.add(label);
    });
    return overlap;
  }

  function isSameBundleProduct(sourceItem, targetItem) {
    return String(sourceItem && sourceItem.id) === String(targetItem && targetItem.id);
  }

  /** Sync when same product ID or normalized label overlap — no config-set IDs in v1. */
  function canSyncBundleLine(sourceItem, targetItem) {
    if (!sourceItem || !targetItem) return false;
    if (isSameBundleProduct(sourceItem, targetItem)) return true;
    return getPersonalizationLabelOverlap(sourceItem, targetItem).size > 0;
  }

  function hasPersonalizationLabelOverlap(sourceItem, targetItem) {
    return getPersonalizationLabelOverlap(sourceItem, targetItem).size > 0;
  }

  function isBundlePropertyValueEmpty(val) {
    if (val == null || val === '') return true;
    if (typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val).length === 0;
    }
    return false;
  }

  function getSlotPropValueByNormalizedLabel(slotProps, normLabel) {
    if (!slotProps || typeof slotProps !== 'object') return undefined;
    var keys = Object.keys(slotProps);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (normalizeBundleFieldLabel(key) !== normLabel) continue;
      var v = slotProps[key];
      if (!isBundlePropertyValueEmpty(v)) return v;
    }
    return undefined;
  }

  function newDesignLabelFromDesignField(designField) {
    if (!designField || !designField.settings) return 'Create from scratch';
    var raw = designField.settings;
    if (typeof raw === 'object' && !Array.isArray(raw) && raw.new_design_label) {
      return String(raw.new_design_label).trim() || 'Create from scratch';
    }
    if (typeof raw === 'string') {
      var match = raw.match(/new_design_label:\s*([^\n]+)/);
      if (match) return match[1].trim() || 'Create from scratch';
    }
    return 'Create from scratch';
  }

  /** PB-FND-84 — copy legacy v1 Design Source/File as v2 field-label prop. */
  function writeLegacyV1DesignAsFieldLabel(filtered, sourceProps, designField, getRaw, empty) {
    if (!designField || !designField.label) return;
    var file = getRaw(sourceProps, 'Design File');
    var source = getRaw(sourceProps, 'Design Source');
    if (empty(file) && empty(source)) return;
    var isScratch = !empty(source) && String(source).toLowerCase().indexOf('scratch') !== -1;
    if (isScratch) {
      filtered[designField.label] = newDesignLabelFromDesignField(designField);
      return;
    }
    if (!empty(file)) filtered[designField.label] = file;
  }

  /** True when slot holds non-empty values for at least one shared label (partial copy). */
  function slotHasCopiedSharedFieldProps(slotProps, sourceItem, targetItem) {
    if (!slotProps || !canSyncBundleLine(sourceItem, targetItem)) return false;
    var overlap = getPersonalizationLabelOverlap(sourceItem, targetItem);
    var labels = Array.from(overlap);
    for (var i = 0; i < labels.length; i++) {
      if (!isBundlePropertyValueEmpty(getSlotPropValueByNormalizedLabel(slotProps, labels[i]))) {
        return true;
      }
    }
    return false;
  }

  /** applyBundleSyncToAll — drop incomplete cross-product slot only when no shared props copied. */
  function shouldDeleteIncompleteCrossProductSlot(ctx) {
    ctx = ctx || {};
    if (ctx.sameProduct || ctx.completeAfter) return false;
    if (ctx.hasP13nCopy) return false;
    if (slotHasCopiedSharedFieldProps(ctx.slotProps, ctx.sourceItem, ctx.targetItem)) return false;
    return true;
  }

  /** pruneIncompleteCrossProductSlots — true when incomplete cross-product slot should be removed. */
  function shouldPruneIncompleteCrossProductSlot(slotProps, sourceItem, targetItem) {
    return !slotHasCopiedSharedFieldProps(slotProps, sourceItem, targetItem);
  }

  /** Ensure bundle configuration bucket exists before seed/sync reads row slots. */
  function ensureBundleConfigurations(state, bundleId) {
    if (!state || bundleId == null || bundleId === '') return {};
    if (!state.bundle) state.bundle = { selectedId: null, configurations: {} };
    if (!state.bundle.configurations) state.bundle.configurations = {};
    const bid = String(bundleId);
    if (!state.bundle.configurations[bid]) state.bundle.configurations[bid] = {};
    return state.bundle.configurations[bid];
  }

  /** Label-filtered copy from source props to target config (cross-product + same-SKU). */
  function copyFilteredP13nFromSourceCore(sourceProps, sourceItem, targetItem) {
    if (!sourceProps || !sourceItem || !targetItem) return {};
    if (targetItem.skipPersonalizationInBundle && !isSameBundleProduct(sourceItem, targetItem)) return {};
    if (!canSyncBundleLine(sourceItem, targetItem)) return {};

    const filtered = {};
    const overlap = getPersonalizationLabelOverlap(sourceItem, targetItem);
    overlap.forEach(function (normLabel) {
      const v = getSlotPropValueByNormalizedLabel(sourceProps, normLabel);
      if (isBundlePropertyValueEmpty(v)) return;
      (targetItem.config || []).forEach(function (field) {
        if (!field || !field.label) return;
        if (normalizeBundleFieldLabel(field.label) === normLabel) {
          filtered[field.label] = v;
        }
      });
    });

    /* Map via source template labels when prop keys match source config but not normalized overlap keys. */
    (targetItem.config || []).forEach(function (targetField) {
      if (!targetField || !targetField.label) return;
      const targetNorm = normalizeBundleFieldLabel(targetField.label);
      var mappedValue;
      (sourceItem.config || []).forEach(function (sourceField) {
        if (!sourceField || !sourceField.label) return;
        if (normalizeBundleFieldLabel(sourceField.label) !== targetNorm) return;
        var v = getSlotPropValueByNormalizedLabel(
          sourceProps,
          normalizeBundleFieldLabel(sourceField.label)
        );
        if (!isBundlePropertyValueEmpty(v)) mappedValue = v;
      });
      if (!isBundlePropertyValueEmpty(mappedValue)) {
        filtered[targetField.label] = mappedValue;
      }
    });

    Object.assign(
      filtered,
      copySharedAiMediaTier1FromSource(sourceProps, sourceItem, targetItem)
    );

    return filtered;
  }

  /**
   * Copy Photo / Character tier-1 keys when bundle lines can sync (matching personalization rule).
   * Cross-product rows share media when canSyncBundleLine — same belt as text field overlap.
   */
  function copySharedAiMediaTier1FromSource(sourceProps, sourceItem, targetItem) {
    const out = {};
    if (!sourceProps || !sourceItem || !targetItem) return out;
    if (!canSyncBundleLine(sourceItem, targetItem)) return out;

    const sourceAi = (sourceItem.config || []).filter(isAiImageField);
    const targetAi = (targetItem.config || []).filter(isAiImageField);
    if (!sourceAi.length || !targetAi.length) return out;

    const sourceField = sourceAi[0];
    const targetField = targetAi[0];
    const sPk = getAiImagePhotoKeyFromField(sourceField);
    const sCk = getAiImageCharacterKeyFromField(sourceField);
    const tPk = getAiImagePhotoKeyFromField(targetField);
    const tCk = getAiImageCharacterKeyFromField(targetField);

    const photo = sPk
      ? getSlotPropValueByNormalizedLabel(sourceProps, normalizeBundleFieldLabel(sPk))
      : undefined;
    const char = sCk
      ? getSlotPropValueByNormalizedLabel(sourceProps, normalizeBundleFieldLabel(sCk))
      : undefined;

    if (!isBundlePropertyValueEmpty(photo) && tPk && isValidAiMediaTier1Value(tPk, photo)) {
      out[tPk] = String(photo).trim();
    }
    if (!isBundlePropertyValueEmpty(char) && tCk && isValidAiMediaTier1Value(tCk, char)) {
      out[tCk] = String(char).trim();
    }
    return out;
  }

  function mediaOwnershipApi() {
    return global.PBMediaLineOwnership || null;
  }

  function targetMissingSharedAiMedia(slotProps, mediaTier1) {
    const ownership = mediaOwnershipApi();
    if (ownership && typeof ownership.targetNeedsOwnedAiMediaGapFill === 'function') {
      return ownership.targetNeedsOwnedAiMediaGapFill(slotProps, mediaTier1);
    }
    if (!mediaTier1 || !Object.keys(mediaTier1).length) return false;
    const props = slotProps || {};
    for (const key of Object.keys(mediaTier1)) {
      const existing = getSlotPropValueByNormalizedLabel(
        props,
        normalizeBundleFieldLabel(key)
      );
      if (isBundlePropertyValueEmpty(existing) || !isValidAiMediaTier1Value(key, existing)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Fan-out Photo/Character to sync-eligible rows — runs even when text fields already copied.
   * PB-AI-10: matching personalization includes AI media tier-1 keys.
   * PB-BRI-1: only anchor/main may fan; gap-fill never overwrites owned sibling URLs.
   */
  function applySharedAiMediaFanOut(params) {
    params = params || {};
    const bundleId = params.bundleId;
    const bundle = params.bundle;
    const state = params.state;
    const sourceIdx = params.sourceIdx;
    const sourceProps = params.sourceProps;
    const mainProduct = params.mainProduct;

    const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
    if (!bid || !bundle || !state || sourceIdx == null || sourceIdx < 0) return false;
    if (!sourceProps || typeof sourceProps !== 'object') return false;

    const ownership = mediaOwnershipApi();
    if (ownership && typeof ownership.canSourceFanOutAiMedia === 'function') {
      if (
        !ownership.canSourceFanOutAiMedia({
          bundle: bundle,
          mainProduct: mainProduct,
          sourceIdx: sourceIdx,
          allowMediaFanOut: params.allowMediaFanOut,
        })
      ) {
        return false;
      }
    }

    const items = bundle.items || [];
    const sourceItem = items[sourceIdx];
    if (!sourceItem) return false;

    const configs = ensureBundleConfigurations(state, bid);
    const buildSlot =
      params.buildSlotProperties ||
      function (props) {
        return props;
      };
    const resolveVariantId =
      params.resolveDefaultVariantId ||
      function (item) {
        return item && item.variants && item.variants[0] ? item.variants[0].id : null;
      };
    const isUserEdited =
      params.isTargetUserEdited ||
      function (_b, idx, s) {
        const slot = s.bundle.configurations[bid][idx];
        return !!(slot && slot.userEdited);
      };
    const expandP13n =
      params.expandP13n ||
      function (p) {
        return p;
      };

    let changed = false;
    for (let t = 0; t < items.length; t += 1) {
      if (t === sourceIdx) continue;
      if (isUserEdited(bid, t, state)) continue;
      const targetItem = items[t];
      if (targetItem.skipPersonalizationInBundle && targetItem.isSimple) continue;
      if (!canSyncBundleLine(sourceItem, targetItem)) continue;

      const mediaTier1 = copySharedAiMediaTier1FromSource(sourceProps, sourceItem, targetItem);
      if (!Object.keys(mediaTier1).length) continue;

      const existing =
        configs[t] && configs[t].properties ? { ...configs[t].properties } : {};
      if (!targetMissingSharedAiMedia(existing, mediaTier1)) continue;

      let merged;
      if (ownership && typeof ownership.gapFillOwnedAiMedia === 'function') {
        const gap = ownership.gapFillOwnedAiMedia(existing, mediaTier1);
        if (!gap.changed) continue;
        merged = gap.props;
      } else {
        merged = { ...existing, ...mediaTier1 };
      }
      let props;
      if (typeof params.encodeSyncedRowProperties === 'function') {
        props = params.encodeSyncedRowProperties(targetItem, expandP13n(merged));
      } else {
        props = buildSlot(merged, targetItem);
      }
      props = purgeInvalidAiMediaTier1Keys(props);
      /* Ownership: after encode, restore any pre-owned media encode may have dropped/overwritten. */
      if (ownership && typeof ownership.gapFillOwnedAiMedia === 'function') {
        props = ownership.gapFillOwnedAiMedia(props, existing).props;
        props = ownership.gapFillOwnedAiMedia(props, mediaTier1).props;
      }
      const variantId = (configs[t] && configs[t].id) || resolveVariantId(targetItem);
      if (!variantId) continue;

      configs[t] = { ...(configs[t] || {}), id: variantId, properties: props };
      changed = true;
    }
    return changed;
  }

  /**
   * PB-AI-11 — Media gap-fill orchestrator. Runs after label copy on every sync trigger.
   * Fills Photo/Character tier-1 on eligible rows even when text fields are already complete.
   */
  function syncBundleMediaFromSource(params) {
    params = params || {};
    const bundleId = params.bundleId;
    const bundle = params.bundle;
    const state = params.state;
    const sourceIdx = params.sourceIdx;
    let sourceProps = params.sourceProps;

    const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
    if (!bid || !bundle || !state || sourceIdx == null || sourceIdx < 0) return false;

    if (!sourceProps || typeof sourceProps !== 'object') {
      if (typeof params.getEffectiveProps === 'function') {
        const mainProduct = params.mainProduct;
        const items = bundle.items || [];
        const sourceItem = items[sourceIdx];
        if (!sourceItem || !mainProduct) return false;
        const firstMainIdx = items.findIndex(function (i) {
          return String(i && i.id) === String(mainProduct.id);
        });
        sourceProps = params.getEffectiveProps(
          bid,
          sourceIdx,
          sourceItem,
          firstMainIdx,
          mainProduct,
          state
        );
      } else {
        const slot = ensureBundleConfigurations(state, bid)[sourceIdx];
        sourceProps = slot && slot.properties ? { ...slot.properties } : null;
      }
    }
    if (!sourceProps || typeof sourceProps !== 'object') return false;

    const fanOutChanged = applySharedAiMediaFanOut({
      bundleId: bid,
      bundle: bundle,
      mainProduct: params.mainProduct,
      state: state,
      sourceIdx: sourceIdx,
      sourceProps: sourceProps,
      allowMediaFanOut: params.allowMediaFanOut,
      expandP13n: params.expandP13n,
      encodeSyncedRowProperties: params.encodeSyncedRowProperties,
      buildSlotProperties: params.buildSlotProperties,
      resolveDefaultVariantId: params.resolveDefaultVariantId,
      isTargetUserEdited: params.isTargetUserEdited,
    });

    let bridged = false;
    if (params.mainProduct && isAnchorBundleRow(bundle, params.mainProduct, sourceIdx)) {
      const configs = ensureBundleConfigurations(state, bid);
      const slot = configs[sourceIdx];
      const anchorProps = (slot && slot.properties) || sourceProps;
      bridged = bridgeAnchorMediaToMainFlow({
        mainProduct: params.mainProduct,
        state: state,
        anchorProps: anchorProps,
      });
    }
    return !!(fanOutChanged || bridged);
  }

  function isAnchorBundleRow(bundle, mainProduct, rowIdx) {
    const idx = Number(rowIdx);
    if (!bundle || !mainProduct || Number.isNaN(idx) || idx < 0) return false;
    const sameId = function (a, b) {
      return String(a && a.id) === String(b && b.id);
    };
    const firstMainIdx = (bundle.items || []).findIndex(function (i) {
      return sameId(i, mainProduct);
    });
    const item = bundle.items && bundle.items[idx];
    return idx === firstMainIdx && item && sameId(item, mainProduct);
  }

  /**
   * PB-AI-12 — Mirror anchor slot Photo/Character into main-flow selections + aiImage draft
   * so single-product offer card and main-flow steps show the same image.
   * @returns {boolean} true when main state was updated
   */
  function bridgeAnchorMediaToMainFlow(params) {
    params = params || {};
    const mainProduct = params.mainProduct;
    const state = params.state;
    const anchorProps = params.anchorProps;
    if (!mainProduct || !state || !anchorProps || typeof anchorProps !== 'object') return false;

    if (!state.main) state.main = { selections: {}, options: [] };
    if (!state.main.selections) state.main.selections = {};

    const adapters = params.adapters || {};
    const getPhotoKey = adapters.getAiImagePhotoKeyFromField || getAiImagePhotoKeyFromField;
    const getCharKey = adapters.getAiImageCharacterKeyFromField || getAiImageCharacterKeyFromField;
    const isAi = adapters.isAiImageField || isAiImageField;
    const empty = adapters.isBundlePropertyValueEmpty || isBundlePropertyValueEmpty;

    const mediaTier1 = {};
    (mainProduct.config || []).forEach(function (field) {
      if (!isAi(field) || !field.label) return;
      const pk = getPhotoKey(field);
      const ck = getCharKey(field);
      if (pk) {
        const v = anchorProps[pk];
        if (!empty(v) && isValidAiMediaTier1Value(pk, v)) mediaTier1[pk] = String(v).trim();
      }
      if (ck) {
        const v = anchorProps[ck];
        if (!empty(v) && isValidAiMediaTier1Value(ck, v)) mediaTier1[ck] = String(v).trim();
      }
    });
    if (!Object.keys(mediaTier1).length) return false;

    let changed = false;
    Object.keys(mediaTier1).forEach(function (key) {
      const existing = state.main.selections[key];
      if (empty(existing) || String(existing).trim() !== mediaTier1[key]) {
        state.main.selections[key] = mediaTier1[key];
        changed = true;
      }
    });

    const aiDraft = aiImageFromSyncedProps(anchorProps, mainProduct, adapters);
    if (aiDraft && aiDraft.byLabel) {
      if (!state.aiImageByLabel) state.aiImageByLabel = { byLabel: {} };
      if (!state.aiImageByLabel.byLabel) state.aiImageByLabel.byLabel = {};
      Object.keys(aiDraft.byLabel).forEach(function (label) {
        const incoming = aiDraft.byLabel[label];
        const existing = state.aiImageByLabel.byLabel[label];
        const incomingChar = ((incoming.characterUrls || [])[0] || '').trim();
        const existingChar = existing ? ((existing.characterUrls || [])[0] || '').trim() : '';
        const incomingPhoto = (incoming.photoUrl || '').trim();
        const existingPhoto = existing ? (existing.photoUrl || '').trim() : '';
        if (incomingChar !== existingChar || incomingPhoto !== existingPhoto) {
          state.aiImageByLabel.byLabel[label] = { ...incoming };
          changed = true;
        }
      });

      (mainProduct.config || []).forEach(function (field) {
        if (!isAi(field) || !field.label) return;
        const pk = getPhotoKey(field);
        const ck = getCharKey(field);
        const nested = {};
        if (pk && mediaTier1[pk]) nested[pk] = mediaTier1[pk];
        if (ck && mediaTier1[ck]) nested[ck] = mediaTier1[ck];
        if (!Object.keys(nested).length) return;
        const prevNested = state.main.selections[field.label];
        const prevJson = prevNested && typeof prevNested === 'object' ? JSON.stringify(prevNested) : '';
        const nextJson = JSON.stringify(nested);
        if (prevJson !== nextJson) {
          state.main.selections[field.label] = nested;
          changed = true;
        }
      });
    }

    return changed;
  }

  /**
   * Rebuild catalogDesignLibrary so cart encode re-emits Design File / Design Source.
   * Sync copies Tier-1 keys into expanded props; encode strips them unless this is set.
   */
  function catalogDesignLibraryFromSyncedProps(props, targetItem, adapters) {
    adapters = adapters || {};
    const raw = props && typeof props === 'object' ? props : {};
    if (typeof adapters.decodeCatalogDesignLibraryFromProps === 'function') {
      const decoded = adapters.decodeCatalogDesignLibraryFromProps(raw, targetItem && targetItem.config);
      if (decoded && decoded.byLabel && Object.keys(decoded.byLabel).length) return decoded;
    }
    const getRaw = adapters.getBundlePropRawByNormalizedKey || function (p, key) {
      const want = normalizeBundleFieldLabel(key);
      for (const k of Object.keys(p || {})) {
        if (normalizeBundleFieldLabel(k) === want) return p[k];
      }
      return undefined;
    };
    const empty = adapters.isBundlePropertyValueEmpty || isBundlePropertyValueEmpty;
    const isDesign = adapters.isDesignLibraryField || function (field) {
      if (!field) return false;
      const type = String(field.type || '').toLowerCase().replace(/\s+/g, ' ');
      if (type.indexOf('design library') !== -1 || type.indexOf('designlibrary') !== -1) return true;
      return normalizeBundleFieldLabel(field.label) === 'design';
    };
    const designFields = (targetItem && targetItem.config || []).filter(isDesign);
    for (let di = 0; di < designFields.length; di += 1) {
      const designField = designFields[di];
      const label = (designField && designField.label) || 'Design';
      const val = getRaw(raw, label);
      if (empty(val)) continue;
      const str = String(val).trim();
      const isScratch = !/^https?:\/\//i.test(str);
      return {
        byLabel: {
          [label]: isScratch
            ? { selectionMode: 'new_design', selectedEntryId: null, previewUrl: '', productionUrl: '', productionFilesBlock: '' }
            : {
                selectionMode: 'catalog',
                selectedEntryId: getRaw(raw, '_design_library_entry_id') || null,
                previewUrl: str,
                productionUrl: getRaw(raw, '_design_production_url') || '',
                // PBPF bundle-row fix: without this, a bundle row (Couple Set, The Perfect
                // Pair) rebuilding its Design Library snapshot from synced Tier-1 props drops
                // the production file list, even though the sibling entry-id/production-url
                // keys above survive the same rebuild.
                productionFilesBlock: getRaw(raw, '_design_production_files') || '',
                designMatch: getRaw(raw, '_design_match') || '',
              },
        },
      };
    }
    const file = getRaw(raw, 'Design File');
    const source = getRaw(raw, 'Design Source');
    if (empty(file) && empty(source)) return undefined;
    const designField = (targetItem && targetItem.config || []).find(isDesign);
    const label = (designField && designField.label) || 'Design';
    const isScratch = !empty(source) && String(source).toLowerCase().indexOf('scratch') !== -1;
    return {
      byLabel: {
        [label]: isScratch
          ? { selectionMode: 'new_design', selectedEntryId: null, previewUrl: '', productionUrl: '', productionFilesBlock: '' }
          : {
              selectionMode: 'catalog',
              selectedEntryId: null,
              previewUrl: empty(file) ? '' : String(file),
              productionUrl: '',
              productionFilesBlock: getRaw(raw, '_design_production_files') || '',
                designMatch: getRaw(raw, '_design_match') || '',
            },
      },
    };
  }

  function parseAiImageFieldSettings(field) {
    if (!field || !field.settings) return {};
    const raw = field.settings;
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return {
        photo_property: raw.photo_property || raw.photoProperty || '',
        character_property: raw.character_property || raw.characterProperty || '',
      };
    }
    const settings = String(raw == null ? '' : raw).trim();
    if (settings.charAt(0) === '{') {
      try {
        const parsed = JSON.parse(settings);
        if (parsed && typeof parsed === 'object') {
          return {
            photo_property: parsed.photo_property || parsed.photoProperty || '',
            character_property: parsed.character_property || parsed.characterProperty || '',
          };
        }
      } catch (e) {}
    }
    const photoMatch = settings.match(/photo_property\s*:\s*([^,\n]+)/i);
    const charMatch = settings.match(/character_property\s*:\s*([^,\n]+)/i);
    return {
      photo_property: photoMatch ? String(photoMatch[1]).trim() : '',
      character_property: charMatch ? String(charMatch[1]).trim() : '',
    };
  }

  function getAiImagePhotoKeyFromField(field) {
    if (!field || !field.type || String(field.type).toLowerCase().indexOf('ai image') === -1) return null;
    const st = parseAiImageFieldSettings(field);
    return (st.photo_property && String(st.photo_property).trim()) || 'Photo';
  }

  function getAiImageCharacterKeyFromField(field) {
    if (!field || !field.type || String(field.type).toLowerCase().indexOf('ai image') === -1) return null;
    const st = parseAiImageFieldSettings(field);
    const custom = st.character_property && String(st.character_property).trim();
    if (custom) return custom;
    return (field.label && String(field.label).trim()) || 'Character';
  }

  function isAiImageField(field) {
    return !!(field && field.type && String(field.type).toLowerCase().indexOf('ai image') !== -1);
  }

  function isLabelAsValueTier1Property(key, value) {
    const k = String(key || '').trim();
    const v = String(value == null ? '' : value).trim();
    if (!k || !v) return false;
    return k.toLowerCase() === v.toLowerCase();
  }

  function isAiMediaTier1Key(key) {
    const k = String(key || '').trim().toLowerCase();
    if (!k) return false;
    if (k === 'photo' || k === 'character') return true;
    if (k.endsWith(' - photo') || k.endsWith(' - character')) return true;
    return false;
  }

  function isDisplayableImageUrl(val) {
    if (typeof val !== 'string' || !val.trim()) return false;
    const u = val.trim();
    if (u.indexOf('http://') === 0 || u.indexOf('https://') === 0 || u.indexOf('//') === 0) return true;
    if (u.indexOf('/uploads/') !== -1 || u.indexOf('/generated/') !== -1) return true;
    return false;
  }

  function isValidAiMediaTier1Value(key, value) {
    if (!isAiMediaTier1Key(key)) return true;
    const v = String(value == null ? '' : value).trim();
    if (!v) return false;
    if (isLabelAsValueTier1Property(key, value)) return false;
    return isDisplayableImageUrl(v);
  }

  function purgeInvalidAiMediaTier1Keys(props) {
    const out = {};
    Object.keys(props || {}).forEach(function (key) {
      const val = props[key];
      if (isAiMediaTier1Key(key) && !isValidAiMediaTier1Value(key, val)) return;
      out[key] = val;
    });
    return out;
  }

  /**
   * Rebuild aiImageByLabel so cart encode re-emits Photo / Character after sync copy.
   * Mirrors catalogDesignLibraryFromSyncedProps (PB-MD-3 · GAP-06).
   */
  function aiImageFromSyncedProps(props, targetItem, adapters) {
    adapters = adapters || {};
    const raw = props && typeof props === 'object' ? props : {};
    const empty = adapters.isBundlePropertyValueEmpty || isBundlePropertyValueEmpty;
    const getRaw = adapters.getBundlePropRawByNormalizedKey || function (p, key) {
      const want = normalizeBundleFieldLabel(key);
      for (const k of Object.keys(p || {})) {
        if (normalizeBundleFieldLabel(k) === want) return p[k];
      }
      return undefined;
    };
    const getPhotoKey = adapters.getAiImagePhotoKeyFromField || getAiImagePhotoKeyFromField;
    const getCharKey = adapters.getAiImageCharacterKeyFromField || getAiImageCharacterKeyFromField;
    const isAi = adapters.isAiImageField || isAiImageField;

    const byLabel = {};
    (targetItem && targetItem.config || []).forEach(function (field) {
      if (!isAi(field) || !field.label) return;
      const pk = getPhotoKey(field);
      const ck = getCharKey(field);
      const photo = pk ? getRaw(raw, pk) : undefined;
      const char = ck ? getRaw(raw, ck) : undefined;
      const validPhoto =
        !empty(photo) && isValidAiMediaTier1Value(pk, photo) ? String(photo).trim() : '';
      const characterUrls = [];
      if (!empty(char) && isValidAiMediaTier1Value(ck, char)) characterUrls.push(String(char).trim());
      if (!validPhoto && !characterUrls.length) return;

      byLabel[field.label] = {
        photoUrl: validPhoto,
        characterUrls: characterUrls,
        selectedIndex: 0,
        generationCount: Math.max(characterUrls.length, validPhoto ? 1 : 0),
      };
    });

    return Object.keys(byLabel).length ? { byLabel: byLabel } : undefined;
  }

  /**
   * Same-SKU duplicate rows — copy full anchor effective props onto target config labels.
   * Cross-product still uses label overlap only (CEO rule A).
   */
  // A copied design is one selection: its identity must travel with its visible URL.
  function copyDesignIdentity(target, source, visible) {
    if (!/^https?:\/\//i.test(String(visible || '').trim())) return;
    ['_design_library_entry_id', '_design_match', '_design_preview_url',
      '_design_production_url', '_design_production_files'].forEach(function (key) {
      if (!isBundlePropertyValueEmpty(source[key])) target[key] = source[key];
    });
  }

  function copySameSkuBundlePropsFromSource(sourceProps, sourceItem, targetItem) {
    if (!sourceProps || !sourceItem || !targetItem) return {};
    if (!isSameBundleProduct(sourceItem, targetItem)) return {};
    const filtered = {};
    (targetItem.config || []).forEach(function (field) {
      if (!field || !field.label) return;
      const type = String(field.type || '').toLowerCase().replace(/\s+/g, ' ');
      const isDesign =
        type.indexOf('design library') !== -1
        || type.indexOf('designlibrary') !== -1
        || normalizeBundleFieldLabel(field.label) === 'design';
      if (isDesign) {
        const display = getSlotPropValueByNormalizedLabel(sourceProps, normalizeBundleFieldLabel(field.label));
        if (!isBundlePropertyValueEmpty(display)) {
          filtered[field.label] = display;
          copyDesignIdentity(filtered, sourceProps, display);
        } else {
          writeLegacyV1DesignAsFieldLabel(
            filtered,
            sourceProps,
            field,
            function (props, key) {
              return getSlotPropValueByNormalizedLabel(props, normalizeBundleFieldLabel(key));
            },
            isBundlePropertyValueEmpty
          );
        }
        return;
      }
      if (type.indexOf('ai image') !== -1) {
        const pk = getAiImagePhotoKeyFromField(field);
        const ck = getAiImageCharacterKeyFromField(field);
        const photo = pk ? getSlotPropValueByNormalizedLabel(sourceProps, normalizeBundleFieldLabel(pk)) : undefined;
        const char = ck ? getSlotPropValueByNormalizedLabel(sourceProps, normalizeBundleFieldLabel(ck)) : undefined;
        if (!isBundlePropertyValueEmpty(photo) && pk && isValidAiMediaTier1Value(pk, photo)) filtered[pk] = photo;
        if (!isBundlePropertyValueEmpty(char) && ck && isValidAiMediaTier1Value(ck, char)) filtered[ck] = char;
        return;
      }
      const norm = normalizeBundleFieldLabel(field.label);
      const v = getSlotPropValueByNormalizedLabel(sourceProps, norm);
      if (!isBundlePropertyValueEmpty(v)) filtered[field.label] = v;
    });
    if (Object.keys(filtered).length) return filtered;
    return copyFilteredP13nFromSourceCore(sourceProps, sourceItem, targetItem);
  }

  /**
   * Whether sync can skip this target row — requires saved slot props, not borrowed main-flow display.
   */
  function isBundleRowSyncTargetSatisfied(slot, userEdited, checkoutReady) {
    if (userEdited) {
      return !!(slot && slot.properties && Object.keys(slot.properties).length);
    }
    return !!(
      checkoutReady
      && slot
      && slot.properties
      && Object.keys(slot.properties).length > 0
    );
  }

  /**
   * Persist main-flow personalization onto the matching bundle anchor row when complete.
   * Returns true when a new anchor slot was written.
   */
  function seedAnchorRowFromMainFlow(params) {
    params = params || {};
    const bundleId = params.bundleId;
    const bundle = params.bundle;
    const mainProduct = params.mainProduct;
    const state = params.state;
    const isAnchorRowComplete = params.isAnchorRowComplete;
    const getEffectiveProps = params.getEffectiveProps;
    const resolveVariantId = params.resolveVariantId;
    const buildSlotProperties = params.buildSlotProperties;

    const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
    if (!bid || !bundle || !mainProduct || !state) return false;
    if (typeof isAnchorRowComplete !== 'function' || typeof getEffectiveProps !== 'function') return false;

    const sameId = function (a, b) {
      return String(a && a.id) === String(b && b.id);
    };
    const idx = (bundle.items || []).findIndex(function (i) {
      return sameId(i, mainProduct);
    });
    if (idx < 0) return false;
    const item = bundle.items[idx];
    if (!item) return false;

    const configs = ensureBundleConfigurations(state, bid);
    const existing = configs[idx];
    if (existing && existing.properties && Object.keys(existing.properties).length) return false;
    if (!isAnchorRowComplete(bid, bundle, idx, mainProduct, state)) return false;

    let props = getEffectiveProps(bid, idx, item, idx, mainProduct, state);
    if (typeof buildSlotProperties === 'function') {
      props = buildSlotProperties(props, item);
    }
    const selections = (state.main && state.main.selections) || {};
    const variantId =
      typeof resolveVariantId === 'function' ? resolveVariantId(item, selections) : null;
    if (!variantId) return false;

    configs[idx] = { id: variantId, properties: props };
    return true;
  }

  /**
   * Copy shared personalization from source row to eligible targets (main-flow / offer-open path).
   * Returns true when at least one target slot was written.
   */
  function applyLabelSyncFromSourceRow(params) {
    params = params || {};
    const bundleId = params.bundleId;
    const bundle = params.bundle;
    const mainProduct = params.mainProduct;
    const state = params.state;
    const sourceIdx = params.sourceIdx;
    const getEffectiveProps = params.getEffectiveProps;
    const buildSlotProperties = params.buildSlotProperties;
    const resolveDefaultVariantId = params.resolveDefaultVariantId;
    const isTargetComplete = params.isTargetComplete;
    const isTargetUserEdited = params.isTargetUserEdited;

    const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
    if (!bid || !bundle || !mainProduct || !state || sourceIdx == null || sourceIdx < 0) return false;
    if (typeof getEffectiveProps !== 'function') return false;

    const items = bundle.items || [];
    const sourceItem = items[sourceIdx];
    if (!sourceItem) return false;

    const sameId = function (a, b) {
      return String(a && a.id) === String(b && b.id);
    };
    const firstMainIdx = items.findIndex(function (i) {
      return sameId(i, mainProduct);
    });
    const configs = ensureBundleConfigurations(state, bid);
    const sourceProps = getEffectiveProps(bid, sourceIdx, sourceItem, firstMainIdx, mainProduct, state);
    let changed = false;

    for (let t = 0; t < items.length; t++) {
      if (t === sourceIdx) continue;
      if (typeof isTargetUserEdited === 'function' && isTargetUserEdited(bid, t, state)) continue;
      if (typeof isTargetComplete === 'function' && isTargetComplete(bundle, t, mainProduct, state)) {
        continue;
      }
      const targetItem = items[t];
      if (targetItem.skipPersonalizationInBundle && targetItem.isSimple) continue;
      if (!canSyncBundleLine(sourceItem, targetItem)) continue;

      let filtered = isSameBundleProduct(sourceItem, targetItem)
        ? copySameSkuBundlePropsFromSource(sourceProps, sourceItem, targetItem)
        : copyFilteredP13nFromSourceCore(sourceProps, sourceItem, targetItem);
      const ownership = mediaOwnershipApi();
      if (
        params.includeAiMedia === false &&
        ownership &&
        typeof ownership.stripAiMediaTier1FromProps === 'function'
      ) {
        filtered = ownership.stripAiMediaTier1FromProps(filtered);
      }
      if (Object.keys(filtered).length === 0) continue;

      const expanded =
        typeof params.expandP13n === 'function'
          ? params.expandP13n(filtered)
          : filtered;
      let props;
      if (typeof params.encodeSyncedRowProperties === 'function') {
        props = params.encodeSyncedRowProperties(targetItem, expanded);
      } else if (typeof buildSlotProperties === 'function') {
        props = buildSlotProperties(filtered, targetItem);
      } else {
        props = expanded;
      }
      const existingTargetProps =
        configs[t] && configs[t].properties ? configs[t].properties : null;
      if (
        ownership &&
        existingTargetProps &&
        typeof ownership.mergeSavedPropsPreservingOwnedMedia === 'function'
      ) {
        props = ownership.mergeSavedPropsPreservingOwnedMedia(existingTargetProps, props);
      }
      const variantId =
        typeof resolveDefaultVariantId === 'function' ? resolveDefaultVariantId(targetItem) : null;
      if (!variantId) continue;

      configs[t] = { id: variantId, properties: props };
      changed = true;
    }
    return changed;
  }

  /**
   * After bundle row sub-flow save — copy shared personalization from the saved row.
   * Testable mirror of finishSubFlow → maybeAutoApplyBundleSync (no DOM).
   */
  function syncMatchingRowsAfterSourceSave(params) {
    params = params || {};
    const bundleId = params.bundleId;
    const bundle = params.bundle;
    const mainProduct = params.mainProduct;
    const state = params.state;
    const sourceIdx = params.sourceIdx;
    const savedProperties = params.savedProperties;
    const variantId = params.variantId;
    const markUserEdited = params.markUserEdited !== false;

    const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
    if (!bid || !bundle || !mainProduct || !state || sourceIdx == null || sourceIdx < 0) return false;
    if (!savedProperties || typeof savedProperties !== 'object') return false;

    const configs = ensureBundleConfigurations(state, bid);
    const ownership = mediaOwnershipApi();
    const priorProps =
      configs[sourceIdx] && configs[sourceIdx].properties
        ? { ...configs[sourceIdx].properties }
        : {};
    let nextProps = { ...savedProperties };
    if (ownership && typeof ownership.mergeSavedPropsPreservingOwnedMedia === 'function') {
      nextProps = ownership.mergeSavedPropsPreservingOwnedMedia(priorProps, nextProps);
    }
    configs[sourceIdx] = {
      id: variantId,
      properties: nextProps,
      userEdited: markUserEdited
    };

    const sourceMayFanMedia =
      !ownership ||
      typeof ownership.canSourceFanOutAiMedia !== 'function' ||
      ownership.canSourceFanOutAiMedia({
        bundle: bundle,
        mainProduct: mainProduct,
        sourceIdx: sourceIdx,
        allowMediaFanOut: params.allowMediaFanOut,
      });

    const labelSynced = applyLabelSyncFromSourceRow({
      bundleId: bid,
      bundle: bundle,
      mainProduct: mainProduct,
      state: state,
      sourceIdx: sourceIdx,
      includeAiMedia: sourceMayFanMedia,
      getEffectiveProps: function (_bid, idx, _item, _firstMainIdx, _mp, s) {
        const slot = s.bundle.configurations[bid][idx];
        return slot && slot.properties ? { ...slot.properties } : {};
      },
      expandP13n: params.expandP13n,
      encodeSyncedRowProperties: params.encodeSyncedRowProperties,
      buildSlotProperties: params.buildSlotProperties || function (props) {
        return props;
      },
      resolveDefaultVariantId:
        params.resolveDefaultVariantId ||
        function (item) {
          return item && item.variants && item.variants[0] ? item.variants[0].id : null;
        },
      isTargetComplete: params.isTargetComplete || function () {
        return false;
      },
      isTargetUserEdited: params.isTargetUserEdited || function (_bid, idx, s) {
        const slot = s.bundle.configurations[bid][idx];
        return !!(slot && slot.userEdited);
      }
    });
    const mediaSynced = sourceMayFanMedia
      ? syncBundleMediaFromSource({
          bundleId: bid,
          bundle: bundle,
          mainProduct: mainProduct,
          state: state,
          sourceIdx: sourceIdx,
          sourceProps: nextProps,
          allowMediaFanOut: params.allowMediaFanOut,
          expandP13n: params.expandP13n,
          encodeSyncedRowProperties: params.encodeSyncedRowProperties,
          buildSlotProperties: params.buildSlotProperties,
          resolveDefaultVariantId: params.resolveDefaultVariantId,
          isTargetUserEdited: params.isTargetUserEdited,
        })
      : false;
    let bridged = false;
    if (isAnchorBundleRow(bundle, mainProduct, sourceIdx)) {
      bridged = bridgeAnchorMediaToMainFlow({
        mainProduct: mainProduct,
        state: state,
        anchorProps: nextProps,
      });
    }
    return !!(labelSynced || mediaSynced || bridged);
  }

  /**
   * Auto-synced row (slot with props, not userEdited) — delegates to unified readiness resolver.
   */
  function isAutoSyncedBundleRowReady(ctx) {
    ctx = ctx || {};
    const slot = ctx.slot;
    const item = ctx.item;
    if (!item || !slot || !slot.properties || typeof slot.properties !== 'object') return false;
    if (Object.keys(slot.properties).length === 0) return false;
    if (ctx.userEdited) return false;

    const resolver = global.PBOfferLineResolver;
    if (resolver && typeof resolver.resolveBundleRowReadiness === 'function') {
      const readiness = resolver.resolveBundleRowReadiness({
        item: item,
        slot: slot,
        userEdited: false,
        needsConfiguration: true,
        effectiveProps: ctx.effectiveProps || slot.properties || {},
        validationFields: item.config || [],
        hasResolvedVariant: ctx.hasResolvedVariant,
        anchorComplete: false,
        getMissingStrictLabels: function () {
          if (typeof ctx.isStrictComplete === 'function' && ctx.isStrictComplete()) return [];
          return ['__incomplete__'];
        },
        isFieldRequired: ctx.isFieldRequired,
        omitMediaField: ctx.omitSubtitleField,
        getPropValueForField: ctx.getPropValueForField,
        isPropertyEmpty: ctx.isPropertyEmpty || isBundlePropertyValueEmpty,
      });
      return !!readiness.autoSyncedReady;
    }

    return false;
  }

  var api = {
    copyDesignIdentity: copyDesignIdentity,
    writeLegacyV1DesignAsFieldLabel: writeLegacyV1DesignAsFieldLabel,
    normalizeBundleFieldLabel: normalizeBundleFieldLabel,
    getPersonalizationTemplateLabelSet: getPersonalizationTemplateLabelSet,
    getPersonalizationLabelOverlap: getPersonalizationLabelOverlap,
    hasPersonalizationLabelOverlap: hasPersonalizationLabelOverlap,
    isSameBundleProduct: isSameBundleProduct,
    canSyncBundleLine: canSyncBundleLine,
    isBundlePropertyValueEmpty: isBundlePropertyValueEmpty,
    slotHasCopiedSharedFieldProps: slotHasCopiedSharedFieldProps,
    shouldDeleteIncompleteCrossProductSlot: shouldDeleteIncompleteCrossProductSlot,
    shouldPruneIncompleteCrossProductSlot: shouldPruneIncompleteCrossProductSlot,
    ensureBundleConfigurations: ensureBundleConfigurations,
    copyFilteredP13nFromSourceCore: copyFilteredP13nFromSourceCore,
    copySharedAiMediaTier1FromSource: copySharedAiMediaTier1FromSource,
    applySharedAiMediaFanOut: applySharedAiMediaFanOut,
    syncBundleMediaFromSource: syncBundleMediaFromSource,
    isAnchorBundleRow: isAnchorBundleRow,
    bridgeAnchorMediaToMainFlow: bridgeAnchorMediaToMainFlow,
    copySameSkuBundlePropsFromSource: copySameSkuBundlePropsFromSource,
    catalogDesignLibraryFromSyncedProps: catalogDesignLibraryFromSyncedProps,
    aiImageFromSyncedProps: aiImageFromSyncedProps,
    isValidAiMediaTier1Value: isValidAiMediaTier1Value,
    purgeInvalidAiMediaTier1Keys: purgeInvalidAiMediaTier1Keys,
    isBundleRowSyncTargetSatisfied: isBundleRowSyncTargetSatisfied,
    seedAnchorRowFromMainFlow: seedAnchorRowFromMainFlow,
    applyLabelSyncFromSourceRow: applyLabelSyncFromSourceRow,
    syncMatchingRowsAfterSourceSave: syncMatchingRowsAfterSourceSave,
    isAutoSyncedBundleRowReady: isAutoSyncedBundleRowReady
  };

  global.PBOfferBundleSync = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);

/**
 * Offer bundle sync orchestration — patches productBuilder (PB-FND-34).
 * Pure helpers live in PBOfferBundleSync above.
 */
(function (global) {
  'use strict';

  function patchProductBuilder(optionalPb) {
    var pb = optionalPb || global.productBuilder;
    if (!pb) return false;

    /* MODULE: OFFER BUNDLE SYNC — auto-copy variants + personalization across bundle lines */
      Object.assign(pb, {
    
          getBundleRowVariantSelections: function(bundleId, idx, item, bundle, mainProduct, s) {
              const sameId = (a, b) => String(a && a.id) === String(b && b.id);
              const slot = s.bundle.configurations[bundleId] && s.bundle.configurations[bundleId][idx];
    
              if (slot && slot.id) {
                  const v = (item.variants || []).find(x => String(x.id) === String(slot.id));
                  if (v) {
                      const sel = {};
                      (item.options || []).forEach((opt, oidx) => {
                          if (opt.name !== 'Title' && v.options && v.options[oidx] != null) {
                              sel[opt.name] = v.options[oidx];
                          }
                      });
                      return sel;
                  }
              }
    
              if (sameId(item, mainProduct)) {
                  const sel = {};
                  (item.options || mainProduct.options || []).forEach(opt => {
                      if (opt && opt.name !== 'Title') {
                          const val = s.main.selections[opt.name];
                          if (val != null && val !== '') sel[opt.name] = val;
                      }
                  });
                  if (Object.keys(sel).length) return sel;
              }
    
              return {};
          },
    
          mergeSourceVariantSelectionsForSync: function(sourceItem, sourceProps, sourceVariantSel) {
              const merged = { ...(sourceVariantSel || {}) };
              (sourceItem.options || []).forEach(function(opt) {
                  if (!opt || opt.name === 'Title') return;
                  if (merged[opt.name] != null && merged[opt.name] !== '') return;
                  const v = sourceProps && sourceProps[opt.name];
                  if (v != null && v !== '') merged[opt.name] = v;
              });
              return merged;
          },
    
          _offerBundleSyncApi: function() {
              return window.PBOfferBundleSync || null;
          },
    
          /** Normalized field labels only (T1 template match — ignores AI sub-keys). */
          getPersonalizationTemplateLabelSet: function(item) {
              const api = this._offerBundleSyncApi();
              if (api && typeof api.getPersonalizationTemplateLabelSet === 'function') {
                  return api.getPersonalizationTemplateLabelSet(item);
              }
              const set = new Set();
              if (!item || item.skipPersonalizationInBundle) return set;
              (item.config || []).forEach(f => {
                  if (f && f.label) set.add(this.normalizeBundleFieldLabel(f.label));
              });
              return set;
          },
    
          hasSamePersonalizationTemplate: function(sourceItem, targetItem) {
              if (!sourceItem || !targetItem) return false;
              if (sourceItem.skipPersonalizationInBundle || targetItem.skipPersonalizationInBundle) return false;
              const srcLabels = this.getPersonalizationTemplateLabelSet(sourceItem);
              const tgtLabels = this.getPersonalizationTemplateLabelSet(targetItem);
              if (tgtLabels.size === 0 || srcLabels.size === 0) return false;
              if (tgtLabels.size !== srcLabels.size) return false;
              for (const label of tgtLabels) {
                  if (!srcLabels.has(label)) return false;
              }
              return true;
          },
    
          getPersonalizationConfigSetIds: function(item) {
              const set = new Set();
              if (!item || !item.configSetIds) return set;
              (item.configSetIds || []).forEach(function(id) {
                  if (id != null && id !== '') set.add(String(id));
              });
              return set;
          },
    
          /** @deprecated Debug only — config-set IDs are not used for eligibility in v1. */
          hasSharedPersonalizationConfigSets: function(sourceItem, targetItem) {
              if (!sourceItem || !targetItem) return false;
              if (sourceItem.skipPersonalizationInBundle || targetItem.skipPersonalizationInBundle) return false;
              const srcIds = this.getPersonalizationConfigSetIds(sourceItem);
              const tgtIds = this.getPersonalizationConfigSetIds(targetItem);
              if (!srcIds.size || !tgtIds.size) return false;
              for (const id of tgtIds) {
                  if (srcIds.has(id)) return true;
              }
              return false;
          },
    
          getPersonalizationLabelOverlap: function(sourceItem, targetItem) {
              const api = this._offerBundleSyncApi();
              if (api && typeof api.getPersonalizationLabelOverlap === 'function') {
                  return api.getPersonalizationLabelOverlap(sourceItem, targetItem);
              }
              const overlap = new Set();
              if (!sourceItem || !targetItem) return overlap;
              if (sourceItem.skipPersonalizationInBundle || targetItem.skipPersonalizationInBundle) return overlap;
              const srcLabels = this.getPersonalizationTemplateLabelSet(sourceItem);
              const tgtLabels = this.getPersonalizationTemplateLabelSet(targetItem);
              for (const label of tgtLabels) {
                  if (srcLabels.has(label)) overlap.add(label);
              }
              return overlap;
          },
    
          hasPersonalizationLabelOverlap: function(sourceItem, targetItem) {
              const api = this._offerBundleSyncApi();
              if (api && typeof api.hasPersonalizationLabelOverlap === 'function') {
                  return api.hasPersonalizationLabelOverlap(sourceItem, targetItem);
              }
              return this.getPersonalizationLabelOverlap(sourceItem, targetItem).size > 0;
          },
    
          isSameBundleProduct: function(sourceItem, targetItem) {
              const api = this._offerBundleSyncApi();
              if (api && typeof api.isSameBundleProduct === 'function') {
                  return api.isSameBundleProduct(sourceItem, targetItem);
              }
              return String(sourceItem && sourceItem.id) === String(targetItem && targetItem.id);
          },
    
          /** Sync when same product ID or normalized label overlap (label-only v1). */
          canSyncBundleLine: function(sourceItem, targetItem) {
              const api = this._offerBundleSyncApi();
              if (api && typeof api.canSyncBundleLine === 'function') {
                  return api.canSyncBundleLine(sourceItem, targetItem);
              }
              if (!sourceItem || !targetItem) return false;
              if (this.isSameBundleProduct(sourceItem, targetItem)) return true;
              return this.hasPersonalizationLabelOverlap(sourceItem, targetItem);
          },
    
          getSharedOptionNames: function(sourceItem, targetItem) {
              const sourceNames = new Set();
              (sourceItem.options || []).forEach(opt => {
                  if (opt && opt.name && opt.name !== 'Title') sourceNames.add(opt.name);
              });
              const shared = [];
              (targetItem.options || []).forEach(opt => {
                  if (opt && opt.name && opt.name !== 'Title' && sourceNames.has(opt.name)) {
                      shared.push(opt.name);
                  }
              });
              return shared;
          },
    
          targetHasOptionValue: function(targetItem, optionName, value) {
              if (value == null || value === '') return false;
              const oidx = (targetItem.options || []).findIndex(opt => opt && opt.name === optionName);
              if (oidx < 0) return false;
              return (targetItem.variants || []).some(v => v.options && v.options[oidx] === value);
          },
    
          buildVariantSelectionsForTarget: function(sourceItem, targetItem, sourceVariantSel) {
              const out = {};
              this.getSharedOptionNames(sourceItem, targetItem).forEach(optName => {
                  const srcVal = sourceVariantSel[optName];
                  if (srcVal == null || srcVal === '') return;
                  if (this.targetHasOptionValue(targetItem, optName, srcVal)) {
                      out[optName] = srcVal;
                  }
              });
              return out;
          },
    
          resolveBundleItemVariantIdStrict: function(item, selections) {
              if (!item || !item.variants || !item.variants.length) return null;
              const sel = selections || {};
              const nonTitleOpts = (item.options || []).filter(opt => opt && opt.name !== 'Title');
              for (let i = 0; i < nonTitleOpts.length; i++) {
                  const opt = nonTitleOpts[i];
                  const val = sel[opt.name];
                  if (val == null || val === '') return null;
              }
              const match = item.variants.find(v => {
                  return (item.options || []).every((opt, idx) => {
                      if (opt.name === 'Title') return true;
                      return v.options[idx] === sel[opt.name];
                  });
              });
              return match ? match.id : null;
          },
    
          isBundleVariantIdAvailable: function(item, variantId) {
              if (!variantId || !item || !item.variants) return false;
              const v = item.variants.find(x => String(x.id) === String(variantId));
              if (!v) return false;
              return v.available !== false;
          },
    
          resolveSyncedBundleVariantId: function(item, selections) {
              let variantId = this.resolveBundleItemVariantIdStrict(item, selections);
              if (!variantId) {
                  variantId = this.resolveBundleItemDefaultVariantId(item);
              }
              if (!variantId) return null;
              if (!this.isBundleVariantIdAvailable(item, variantId)) return null;
              return variantId;
          },
    
          hasBundleVariantSyncOverlap: function(sourceItem, targetItem, sourceVariantSel) {
              return Object.keys(this.buildVariantSelectionsForTarget(sourceItem, targetItem, sourceVariantSel)).length > 0;
          },
    
          copyFilteredP13nFromSource: function(sourceProps, sourceItem, targetItem) {
              const sameProduct = this.isSameBundleProduct(sourceItem, targetItem);
              if (targetItem.skipPersonalizationInBundle && !sameProduct) return {};
              /* Eligibility is config-label overlap (canSyncBundleLine). Do not gate on raw prop-key overlap — main-flow keys may differ from target labels while configs still align. */
              if (!sameProduct && !this.canSyncBundleLine(sourceItem, targetItem)) return {};
    
              const eff = this.expandBundleSyncSourceProps
                  ? this.expandBundleSyncSourceProps(sourceProps)
                  : (this.expandDelimiterProperties ? this.expandDelimiterProperties({ ...sourceProps }) : { ...sourceProps });
              if (sameProduct) {
                  const syncApi = this._offerBundleSyncApi();
                  if (syncApi && typeof syncApi.copySameSkuBundlePropsFromSource === 'function') {
                      const skuCopy = syncApi.copySameSkuBundlePropsFromSource(eff, sourceItem, targetItem);
                      if (Object.keys(skuCopy).length) return skuCopy;
                  }
              }
              const filtered = {};
              const targetKeySet = this.getBundleCopyTargetKeySet(targetItem);
    
              Object.keys(eff).forEach(function(key) {
                  if (this.isBundlePropertyValueEmpty(eff[key])) return;
                  const nk = this.normalizeBundleFieldLabel(key);
                  if (!targetKeySet.has(nk)) return;
                  let outKey = key;
                  (targetItem.config || []).forEach(function(field) {
                      if (!field || !field.label) return;
                      if (this.normalizeBundleFieldLabel(field.label) === nk) outKey = field.label;
                      const pk = this.getAiImagePhotoKeyFromField(field);
                      const ck = this.getAiImageCharacterKeyFromField(field);
                      if (pk && this.normalizeBundleFieldLabel(pk) === nk) outKey = pk;
                      if (ck && this.normalizeBundleFieldLabel(ck) === nk) outKey = ck;
                  }, this);
                  filtered[outKey] = eff[key];
              }, this);
    
              (targetItem.config || []).forEach(function(field) {
                  if (!field) return;
                  if (field.type && String(field.type).toLowerCase().includes('ai image')) {
                      const pk = this.getAiImagePhotoKeyFromField(field);
                      const ck = this.getAiImageCharacterKeyFromField(field);
                      const sAiField = (sourceItem.config || []).find(function(f) {
                          return f && f.type && String(f.type).toLowerCase().includes('ai image');
                      });
                      const sPk = sAiField && this.getAiImagePhotoKeyFromField(sAiField);
                      const sCk = sAiField && this.getAiImageCharacterKeyFromField(sAiField);
                      const pv = sPk ? this.getBundlePropRawByNormalizedKey(eff, sPk) : (pk ? this.getBundlePropRawByNormalizedKey(eff, pk) : undefined);
                      const cv = sCk ? this.getBundlePropRawByNormalizedKey(eff, sCk) : (ck ? this.getBundlePropRawByNormalizedKey(eff, ck) : undefined);
                      if (pk && !this.isBundlePropertyValueEmpty(pv)) filtered[pk] = pv;
                      if (ck && !this.isBundlePropertyValueEmpty(cv)) filtered[ck] = cv;
                  } else if (typeof this.isDesignLibraryField === 'function' && this.isDesignLibraryField(field)) {
                      if (field.label) {
                          const display = this.getBundlePropValueForFieldLabel(eff, field);
                          if (!this.isBundlePropertyValueEmpty(display)) {
                              filtered[field.label] = display;
                              this._offerBundleSyncApi().copyDesignIdentity(filtered, eff, display);
                          } else {
                              this._offerBundleSyncApi().writeLegacyV1DesignAsFieldLabel(
                                  filtered,
                                  eff,
                                  field,
                                  this.getBundlePropRawByNormalizedKey.bind(this),
                                  this.isBundlePropertyValueEmpty.bind(this)
                              );
                          }
                      }
                  } else if (field.label) {
                      const mapped = this.getBundleSyncPropValueForTargetField
                          ? this.getBundleSyncPropValueForTargetField(eff, sourceItem, field)
                          : null;
                      if (mapped && mapped.key) {
                          filtered[mapped.key] = mapped.value;
                      } else {
                          const v = this.getBundlePropValueForFieldLabel(eff, field);
                          if (!this.isBundlePropertyValueEmpty(v)) filtered[field.label] = v;
                      }
                  }
              }, this);
    
              return filtered;
          },
    
          /** @deprecated Use hasSamePersonalizationTemplate / canSyncBundleLine. */
          hasIdenticalPersonalizationProfile: function(sourceItem, targetItem) {
              return this.hasSamePersonalizationTemplate(sourceItem, targetItem);
          },
    
          resolveBundleItemDefaultVariantId: function(item) {
              if (!item || !item.variants || !item.variants.length) return null;
              const nonTitleOpts = (item.options || []).filter(opt => opt && opt.name !== 'Title');
              if (item.isSimple || nonTitleOpts.length === 0) {
                  const v0 = item.variants[0];
                  return v0 && this.isBundleVariantIdAvailable(item, v0.id) ? v0.id : null;
              }
              return null;
          },
    
          /**
           * Rebuild catalogDesignLibrary for encode so Design File / Design Source
           * survive stripDesignLibraryTier1KeysFromSelections (PB-FND-22 Couple Set).
           */
          catalogDesignLibraryFromSyncedProps: function(props, targetItem) {
              const api = this._offerBundleSyncApi && this._offerBundleSyncApi();
              const fn = api && api.catalogDesignLibraryFromSyncedProps;
              if (typeof fn !== 'function') return undefined;
              return fn(props, targetItem, {
                  decodeCatalogDesignLibraryFromProps: this.decodeCatalogDesignLibraryFromProps
                      ? this.decodeCatalogDesignLibraryFromProps.bind(this)
                      : null,
                  getBundlePropRawByNormalizedKey: this.getBundlePropRawByNormalizedKey
                      ? this.getBundlePropRawByNormalizedKey.bind(this)
                      : null,
                  isBundlePropertyValueEmpty: this.isBundlePropertyValueEmpty
                      ? this.isBundlePropertyValueEmpty.bind(this)
                      : null,
                  isDesignLibraryField: this.isDesignLibraryField
                      ? this.isDesignLibraryField.bind(this)
                      : null,
              });
          },

          aiImageFromSyncedProps: function(props, targetItem) {
              const api = this._offerBundleSyncApi && this._offerBundleSyncApi();
              const fn = api && api.aiImageFromSyncedProps;
              if (typeof fn !== 'function') return undefined;
              return fn(props, targetItem, {
                  getBundlePropRawByNormalizedKey: this.getBundlePropRawByNormalizedKey
                      ? this.getBundlePropRawByNormalizedKey.bind(this)
                      : null,
                  isBundlePropertyValueEmpty: this.isBundlePropertyValueEmpty
                      ? this.isBundlePropertyValueEmpty.bind(this)
                      : null,
                  getAiImagePhotoKeyFromField: this.getAiImagePhotoKeyFromField
                      ? this.getAiImagePhotoKeyFromField.bind(this)
                      : null,
                  getAiImageCharacterKeyFromField: this.getAiImageCharacterKeyFromField
                      ? this.getAiImageCharacterKeyFromField.bind(this)
                      : null,
                  isAiImageField: function(field) {
                      return !!(field && field.type && String(field.type).toLowerCase().indexOf('ai image') !== -1);
                  },
              });
          },

          buildSyncedBundleRowProperties: function(targetItem, expandedP13n) {
              if (typeof window !== 'undefined' && window.PBBundleRowEncode && typeof window.PBBundleRowEncode.encodeBundleRowProperties === 'function') {
                  return window.PBBundleRowEncode.encodeBundleRowProperties(targetItem, expandedP13n, this, { trustedAiTextProps: true });
              }
              const syncApi = global.PBOfferBundleSync;
              const isValidAiMedia = function (key, value) {
                  if (typeof this.isValidAiMediaTier1Value === 'function') {
                      return this.isValidAiMediaTier1Value(key, value);
                  }
                  if (syncApi && typeof syncApi.isValidAiMediaTier1Value === 'function') {
                      return syncApi.isValidAiMediaTier1Value(key, value);
                  }
                  return true;
              }.bind(this);
              const purgeInvalidAiMedia = function (props) {
                  if (typeof this.purgeInvalidAiMediaTier1Keys === 'function') {
                      return this.purgeInvalidAiMediaTier1Keys(props);
                  }
                  if (syncApi && typeof syncApi.purgeInvalidAiMediaTier1Keys === 'function') {
                      return syncApi.purgeInvalidAiMediaTier1Keys(props);
                  }
                  return props;
              }.bind(this);
              const sourceProps = expandedP13n && typeof expandedP13n === 'object' ? { ...expandedP13n } : {};
              const nested = this.nestBundleSelectionsForAiImageFields(targetItem, sourceProps);
              const catalogDesignLibrary = this.catalogDesignLibraryFromSyncedProps(sourceProps, targetItem);
              const aiImageByLabel = this.aiImageFromSyncedProps(sourceProps, targetItem);
              if (typeof this.encodeFlowStateForCart === 'function') {
                  const encoded = this.encodeFlowStateForCart(
                      {
                          selections: nested,
                          topicSelections: {},
                          catalogDesignLibrary: catalogDesignLibrary,
                          aiImageByLabel: aiImageByLabel,
                      },
                      targetItem,
                      targetItem.options || []
                  );
                  /* Belt-and-suspenders: never drop Tier-1 keys the sync copy already proved. */
                  if (catalogDesignLibrary && encoded && typeof encoded === 'object') {
                      let tier1 = {};
                      if (typeof this.encodeDesignLibraryPropsFromState === 'function') {
                          tier1 = this.encodeDesignLibraryPropsFromState(
                              { catalogDesignLibrary: catalogDesignLibrary, selections: sourceProps },
                              targetItem.config || []
                          );
                      } else if (typeof this.catalogDesignLibraryToTier1Props === 'function') {
                          tier1 = this.catalogDesignLibraryToTier1Props(catalogDesignLibrary, sourceProps);
                      }
                      const empty = typeof this.isBundlePropertyValueEmpty === 'function'
                          ? this.isBundlePropertyValueEmpty.bind(this)
                          : function(v) { return v == null || v === ''; };
                      Object.keys(tier1 || {}).forEach(function(key) {
                          if (!empty(tier1[key]) && empty(encoded[key])) encoded[key] = tier1[key];
                      });
                  }
                  if (aiImageByLabel && encoded && typeof encoded === 'object') {
                      const empty = typeof this.isBundlePropertyValueEmpty === 'function'
                          ? this.isBundlePropertyValueEmpty.bind(this)
                          : function(v) { return v == null || v === ''; };
                      (targetItem.config || []).forEach(function(field) {
                          if (!field || !field.type || String(field.type).toLowerCase().indexOf('ai image') === -1) return;
                          const pk = this.getAiImagePhotoKeyFromField(field);
                          const ck = this.getAiImageCharacterKeyFromField(field);
                          const photo = pk && typeof this.getBundlePropRawByNormalizedKey === 'function'
                              ? this.getBundlePropRawByNormalizedKey(sourceProps, pk)
                              : (pk ? sourceProps[pk] : undefined);
                          const char = ck && typeof this.getBundlePropRawByNormalizedKey === 'function'
                              ? this.getBundlePropRawByNormalizedKey(sourceProps, ck)
                              : (ck ? sourceProps[ck] : undefined);
                          if (pk && !empty(photo) && empty(encoded[pk]) && isValidAiMedia(pk, photo)) encoded[pk] = photo;
                          if (ck && !empty(char) && empty(encoded[ck]) && isValidAiMedia(ck, char)) encoded[ck] = char;
                      }, this);
                  }
                  return purgeInvalidAiMedia(encoded);
              }
              throw new Error('Personalization could not be saved. Please refresh the page and try again.');
          },
    
          getBundleSyncPropValueForTargetField: function(sourceEff, sourceItem, targetField) {
              if (!targetField) return null;
              let v = this.getBundlePropValueForFieldLabel(sourceEff, targetField);
              if (!this.isBundlePropertyValueEmpty(v)) return { key: targetField.label, value: v };
              const targetNorm = this.normalizeBundleFieldLabel(targetField.label);
              for (let i = 0; i < (sourceItem.config || []).length; i++) {
                  const sf = sourceItem.config[i];
                  if (!sf || !sf.label) continue;
                  if (this.normalizeBundleFieldLabel(sf.label) !== targetNorm) continue;
                  v = this.getBundlePropValueForFieldLabel(sourceEff, sf);
                  if (!this.isBundlePropertyValueEmpty(v)) return { key: targetField.label, value: v };
              }
              return null;
          },
    
          pruneNonSyncableBundleSlots: function(bundleId) {
              const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
              if (!bid) return false;
              const s = window.builderState;
              const mainProduct = window.BUILDER_DATA.mainProduct;
              const bundle = window.BUILDER_DATA.bundles.find(b => String(b && b.id) === bid);
              if (!bundle || !s.bundle.configurations[bid]) return false;
              const sourceIdx = this.resolveBundleSourceIndex(bundle, mainProduct, s);
              if (sourceIdx < 0) return false;
              const sourceItem = bundle.items[sourceIdx];
              let changed = false;
              for (let t = 0; t < bundle.items.length; t++) {
                  if (t === sourceIdx) continue;
                  const existingSlot = s.bundle.configurations[bid][t];
                  if (typeof this.isBundleRowUserEdited === 'function' && this.isBundleRowUserEdited(bid, t, s)) {
                      continue;
                  }
                  if (this.isBundleRowComplete(bundle.id, bundle, t, mainProduct, s)) continue;
                  const targetItem = bundle.items[t];
                  if (!this.canSyncBundleLine(sourceItem, targetItem) && s.bundle.configurations[bid][t]) {
                      delete s.bundle.configurations[bid][t];
                      changed = true;
                  }
              }
              return changed;
          },
    
          /** Drop poisoned incomplete cross-product slots — keep rows with valid partial shared-field copies. */
          pruneIncompleteCrossProductSlots: function(bundleId) {
              const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
              if (!bid) return false;
              const s = window.builderState;
              const mainProduct = window.BUILDER_DATA.mainProduct;
              const bundle = window.BUILDER_DATA.bundles.find(b => String(b && b.id) === bid);
              if (!bundle || !s.bundle.configurations[bid]) return false;
              const sourceIdx = this.resolveBundleSourceIndex(bundle, mainProduct, s);
              if (sourceIdx < 0) return false;
              const sourceItem = bundle.items[sourceIdx];
              let changed = false;
              for (let t = 0; t < bundle.items.length; t++) {
                  if (t === sourceIdx) continue;
                  const existingSlot = s.bundle.configurations[bid][t];
                  if (typeof this.isBundleRowUserEdited === 'function' && this.isBundleRowUserEdited(bid, t, s)) {
                      continue;
                  }
                  if (this.isBundleRowComplete(bundle.id, bundle, t, mainProduct, s)) continue;
                  const targetItem = bundle.items[t];
                  if (this.isSameBundleProduct(sourceItem, targetItem)) continue;
                  const slot = s.bundle.configurations[bid][t];
                  if (!slot) continue;
                  const slotProps = slot.properties || {};
                  const api = this._offerBundleSyncApi();
                  const shouldPrune = api && typeof api.shouldPruneIncompleteCrossProductSlot === 'function'
                      ? api.shouldPruneIncompleteCrossProductSlot(slotProps, sourceItem, targetItem)
                      : !this.hasPersonalizationLabelOverlap(sourceItem, targetItem);
                  if (shouldPrune) {
                      delete s.bundle.configurations[bid][t];
                      changed = true;
                  }
              }
              return changed;
          },
    
          isBundleSyncDebugEnabled: function() {
              try {
                  if (window.BUILDER_DATA && window.BUILDER_DATA.debugBundleSync) return true;
                  if (typeof localStorage !== 'undefined' && localStorage.getItem('pb_debug_bundle_sync') === '1') return true;
              } catch (e) {}
              return false;
          },
    
          debugBundleSyncState: function(bundleId) {
              if (!this.isBundleSyncDebugEnabled()) return;
              const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
              const s = window.builderState;
              const mainProduct = window.BUILDER_DATA.mainProduct;
              const bundle = window.BUILDER_DATA.bundles.find(b => String(b && b.id) === bid);
              if (!bundle) return;
              const sourceIdx = this.resolveBundleSourceIndex(bundle, mainProduct, s);
              const sourceItem = sourceIdx >= 0 ? bundle.items[sourceIdx] : null;
              console.group('[PB] bundle sync debug', bid);
              bundle.items.forEach((item, idx) => {
                  const slot = s.bundle.configurations[bid] && s.bundle.configurations[bid][idx];
                  console.log('row', idx, item.title, {
                      configSetIds: item.configSetIds || [],
                      labels: Array.from(this.getPersonalizationTemplateLabelSet(item)),
                      canSync: sourceItem ? this.canSyncBundleLine(sourceItem, item) : null,
                      exactTemplate: sourceItem ? this.hasSamePersonalizationTemplate(sourceItem, item) : null,
                      complete: this.isBundleRowComplete(bundle.id, bundle, idx, mainProduct, s),
                      slotKeys: slot && slot.properties ? Object.keys(slot.properties) : []
                  });
              });
              console.groupEnd();
          },
    
          shouldAutoApplyBundleSync: function(bundle, mainProduct, s, opts) {
              opts = opts || {};
              const bid = String(bundle && bundle.id || '');
              if (!opts.ignoreSelected && String(s.bundle.selectedId || '') !== bid) {
                  return false;
              }
              const api = this._offerBundleSyncApi();
              if (api && typeof api.ensureBundleConfigurations === 'function') {
                  api.ensureBundleConfigurations(s, bid);
              } else if (!s.bundle.configurations[bid]) {
                  s.bundle.configurations[bid] = {};
              }
              const sourceIdx = this.resolveBundleSyncSourceIndex
                  ? this.resolveBundleSyncSourceIndex(bundle, mainProduct, s)
                  : this.resolveBundleSourceIndex(bundle, mainProduct, s);
              if (sourceIdx < 0) {
                  return false;
              }
              const firstMainIdx = bundle.items.findIndex(i => String(i.id) === String(mainProduct.id));
              const sourceItem = bundle.items[sourceIdx];
              const sourceProps = this.getEffectiveBundleRowProperties(bundle.id, sourceIdx, sourceItem, firstMainIdx, mainProduct, s);
              const sourceEff = this.expandBundleSyncSourceProps
                  ? this.expandBundleSyncSourceProps(sourceProps)
                  : sourceProps;
              const sourceVariantSel = this.getBundleRowVariantSelections(bundle.id, sourceIdx, sourceItem, bundle, mainProduct, s);
              const hasP13n = this.propertyKeysWithValues(sourceEff).length > 0;
              const hasVariants = Object.keys(sourceVariantSel).length > 0;
    
              if (!hasP13n && !hasVariants) {
                  return false;
              }
    
              for (let t = 0; t < bundle.items.length; t++) {
                  if (t === sourceIdx) continue;
                  const existingSlot = s.bundle.configurations[bid][t];
                  if (typeof this.isBundleRowUserEdited === 'function' && this.isBundleRowUserEdited(bid, t, s)) {
                      continue;
                  }
                  if (this.isBundleRowComplete(bundle.id, bundle, t, mainProduct, s)) continue;
                  const targetItem = bundle.items[t];
                  if (targetItem.skipPersonalizationInBundle && targetItem.isSimple) continue;
                  if (!this.canSyncBundleLine(sourceItem, targetItem)) continue;
                  if (this.isSameBundleProduct(sourceItem, targetItem) && (hasP13n || hasVariants)) {
                      return true;
                  }
                  if (hasVariants && this.hasBundleVariantSyncOverlap(sourceItem, targetItem, sourceVariantSel)) {
                      return true;
                  }
                  if (hasP13n && this.hasBundleLabelOverlap(sourceProps, targetItem)) {
                      return true;
                  }
                  if (hasP13n && this.hasPersonalizationLabelOverlap(sourceItem, targetItem)) {
                      return true;
                  }
              }
              return false;
          },
    
          applyBundleSyncToAll: function(bundleId, opts) {
              opts = opts || {};
              const pb = this;
              const s = window.builderState;
              const mainProduct = window.BUILDER_DATA.mainProduct;
              const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
              if (!bid) return false;
              const bundle = window.BUILDER_DATA.bundles.find(b => String(b && b.id) === bid);
              if (!bundle) return false;
              let changed = this.pruneNonSyncableBundleSlots(bid);
              if (typeof this.pruneIncompleteCrossProductSlots === 'function' && this.pruneIncompleteCrossProductSlots(bid)) {
                  changed = true;
              }
              const sourceIdx = this.resolveBundleSyncSourceIndex
                  ? this.resolveBundleSyncSourceIndex(bundle, mainProduct, s)
                  : this.resolveBundleSourceIndex(bundle, mainProduct, s);
              if (sourceIdx < 0) {
                  if (changed && opts.render !== false) {
                      this.renderOffers({ intro: 'skip' });
                      this.renderAddons();
                      this.checkOfferValidity();
                  }
                  if (this.isBundleSyncDebugEnabled()) this.debugBundleSyncState(bid);
                  return changed;
              }
              const firstMainIdx = bundle.items.findIndex(i => String(i.id) === String(mainProduct.id));
              const sourceItem = bundle.items[sourceIdx];
              const sourceProps = this.getEffectiveBundleRowProperties(bid, sourceIdx, sourceItem, firstMainIdx, mainProduct, s);
              const sourceVariantSel = this.getBundleRowVariantSelections(bid, sourceIdx, sourceItem, bundle, mainProduct, s);
              if (!s.bundle.configurations[bid]) s.bundle.configurations[bid] = {};
    
              for (let t = 0; t < bundle.items.length; t++) {
                  if (t === sourceIdx) continue;
                  const existingSlot = s.bundle.configurations[bid][t];
                  if (typeof this.isBundleRowUserEdited === 'function' && this.isBundleRowUserEdited(bid, t, s)) {
                      continue;
                  }
                  const syncApi = this._offerBundleSyncApi();
                  const checkoutReady = typeof this.isBundleRowCheckoutReady === 'function'
                      && this.isBundleRowCheckoutReady(bid, bundle, t, mainProduct, s);
                  if (syncApi && typeof syncApi.isBundleRowSyncTargetSatisfied === 'function') {
                      if (syncApi.isBundleRowSyncTargetSatisfied(existingSlot, false, checkoutReady)) continue;
                  } else if (this.isBundleRowComplete(bundle.id, bundle, t, mainProduct, s)) {
                      continue;
                  }
    
                  const targetItem = bundle.items[t];
                  if (targetItem.skipPersonalizationInBundle && targetItem.isSimple) continue;
    
                  if (!this.canSyncBundleLine(sourceItem, targetItem)) {
                      continue;
                  }
    
                  const sameProduct = this.isSameBundleProduct(sourceItem, targetItem);
                  const mergedSourceVariantSel = this.mergeSourceVariantSelectionsForSync
                      ? this.mergeSourceVariantSelectionsForSync(sourceItem, sourceProps, sourceVariantSel)
                      : sourceVariantSel;
                  let variantSelections = this.buildVariantSelectionsForTarget(sourceItem, targetItem, mergedSourceVariantSel);
                  if (sameProduct && Object.keys(variantSelections).length === 0 && Object.keys(mergedSourceVariantSel).length > 0) {
                      variantSelections = { ...mergedSourceVariantSel };
                  }
                  const filteredP13n = this.copyFilteredP13nFromSource(sourceProps, sourceItem, targetItem);
                  const hasVariantCopy = Object.keys(variantSelections).length > 0;
                  const hasP13nCopy = Object.keys(filteredP13n).length > 0;
    
                  if (!hasVariantCopy && !hasP13nCopy && !sameProduct) continue;
    
                  const expandedP13n = hasP13nCopy
                      ? (this.expandDelimiterProperties ? this.expandDelimiterProperties(filteredP13n) : filteredP13n)
                      : {};
                  let selections = { ...variantSelections, ...expandedP13n };
                  (targetItem.options || []).forEach(function(opt) {
                      if (!opt || opt.name === 'Title') return;
                      if (selections[opt.name] != null && selections[opt.name] !== '') return;
                      const srcVal = mergedSourceVariantSel[opt.name];
                      if (srcVal != null && srcVal !== '') selections[opt.name] = srcVal;
                  });
                  selections = this.nestBundleSelectionsForAiImageFields(targetItem, selections);
                  let variantId = this.resolveSyncedBundleVariantId(targetItem, selections);
                  if (!variantId && sameProduct && typeof this.resolveBundleItemVariantId === 'function') {
                      variantId = this.resolveBundleItemVariantId(targetItem, mergedSourceVariantSel);
                      if (variantId && !this.isBundleVariantIdAvailable(targetItem, variantId)) variantId = null;
                  }
                  const cleanProps = hasP13nCopy
                      ? this.buildSyncedBundleRowProperties(targetItem, expandedP13n)
                      : {};
    
                  if (variantId) {
                      s.bundle.configurations[bid][t] = { id: variantId, properties: cleanProps };
                      const completeAfter = this.isBundleRowComplete(bundle.id, bundle, t, mainProduct, s);
                      const syncApi = this._offerBundleSyncApi();
                      const shouldDelete = syncApi && typeof syncApi.shouldDeleteIncompleteCrossProductSlot === 'function'
                          ? syncApi.shouldDeleteIncompleteCrossProductSlot({
                              sameProduct: sameProduct,
                              completeAfter: completeAfter,
                              hasP13nCopy: hasP13nCopy,
                              slotProps: cleanProps,
                              sourceItem: sourceItem,
                              targetItem: targetItem
                          })
                          : (!sameProduct && !completeAfter && !hasP13nCopy);
                      if (shouldDelete) {
                          delete s.bundle.configurations[bid][t];
                      } else {
                          changed = true;
                      }
                  } else if (hasP13nCopy) {
                      const fallbackId = this.resolveBundleItemDefaultVariantId(targetItem);
                      if (fallbackId) {
                          s.bundle.configurations[bid][t] = { id: fallbackId, properties: cleanProps };
                          changed = true;
                      }
                  }
              }

              const syncApiAfter = this._offerBundleSyncApi();
              if (syncApiAfter && typeof syncApiAfter.syncBundleMediaFromSource === 'function') {
                  const mediaChanged = syncApiAfter.syncBundleMediaFromSource({
                      bundleId: bid,
                      bundle: bundle,
                      mainProduct: mainProduct,
                      state: s,
                      sourceIdx: sourceIdx,
                      sourceProps: sourceProps,
                      getEffectiveProps: function (b, idx, item, firstMainIdx, mp, st) {
                          return pb.getEffectiveBundleRowProperties(b, idx, item, firstMainIdx, mp, st);
                      },
                      expandP13n: function (p) {
                          return pb.expandDelimiterProperties ? pb.expandDelimiterProperties(p) : p;
                      },
                      encodeSyncedRowProperties: function (targetItem, expanded) {
                          return pb.buildSyncedBundleRowProperties(targetItem, expanded);
                      },
                      buildSlotProperties: function (props, targetItem) {
                          return pb.buildSyncedBundleRowProperties(targetItem, props);
                      },
                      resolveDefaultVariantId: function (item) {
                          return pb.resolveBundleItemDefaultVariantId(item);
                      },
                      isTargetUserEdited: function (b, idx, st) {
                          return typeof pb.isBundleRowUserEdited === 'function'
                              && pb.isBundleRowUserEdited(b, idx, st);
                      },
                  });
                  if (mediaChanged) changed = true;
              }
    
              if (this.isBundleSyncDebugEnabled()) this.debugBundleSyncState(bid);
    
              if (changed && opts.render !== false) {
                  this.renderOffers({ intro: 'skip' });
                  this.renderAddons();
                  this.checkOfferValidity();
              }
              return changed;
          },
    
          maybeAutoApplyBundleSync: function(bundleId, opts) {
              opts = opts || {};
              const bid = bundleId != null && bundleId !== '' ? String(bundleId) : '';
              if (!bid) return false;
              const s = window.builderState;
              const mainProduct = window.BUILDER_DATA.mainProduct;
              const bundle = window.BUILDER_DATA.bundles.find(b => String(b && b.id) === bid);
              if (!bundle) return false;
              const api = this._offerBundleSyncApi();
              if (api && typeof api.ensureBundleConfigurations === 'function') {
                  api.ensureBundleConfigurations(s, bid);
              } else if (!s.bundle.configurations[bid]) {
                  s.bundle.configurations[bid] = {};
              }
              const should = this.shouldAutoApplyBundleSync(bundle, mainProduct, s, opts);
              if (!should) {
                  return false;
              }
              const changed = this.applyBundleSyncToAll(bid, { render: false });
              if (changed && typeof this.invalidateOffersListContentHash === 'function') {
                  this.invalidateOffersListContentHash();
              }
              return changed;
          },
    
          /** Seed + sync every eligible bundle when main flow is complete (offer step first paint). */
          maybeAutoApplyBundleSyncAll: function(opts) {
              opts = opts || {};
              const s = window.builderState;
              const mainProduct = window.BUILDER_DATA && window.BUILDER_DATA.mainProduct;
              const bundles = (window.BUILDER_DATA && window.BUILDER_DATA.bundles) || [];
              if (!mainProduct || !bundles.length) return false;
              if (typeof this.isStandaloneMainFlowComplete === 'function'
                  && !this.isStandaloneMainFlowComplete(mainProduct, s)) {
                  return false;
              }
              let any = false;
              const pb = this;
              bundles.forEach(function(b) {
                  if (!b || b.id == null || b.id === '') return;
                  const bid = String(b.id);
                  if (typeof pb.seedAnchorBundleRowFromMain === 'function') {
                      if (pb.seedAnchorBundleRowFromMain(bid, b, mainProduct, s)) any = true;
                  }
                  if (typeof pb.maybeAutoApplyBundleSync === 'function') {
                      if (pb.maybeAutoApplyBundleSync(bid, { ignoreSelected: true })) any = true;
                  }
              });
              if (typeof pb.maybeBridgeAnchorMediaToMain === 'function') {
                  if (pb.maybeBridgeAnchorMediaToMain(mainProduct, s)) any = true;
              }
              if (any && typeof this.invalidateOffersListContentHash === 'function') {
                  this.invalidateOffersListContentHash();
              }
              return any;
          },

          /** PB-AI-12 — Mirror anchor slot media into main flow for single-offer thumb parity. */
          maybeBridgeAnchorMediaToMain: function(mainProduct, s) {
              const state = s || window.builderState;
              const mp = mainProduct || (window.BUILDER_DATA && window.BUILDER_DATA.mainProduct);
              const bundles = (window.BUILDER_DATA && window.BUILDER_DATA.bundles) || [];
              const api = this._offerBundleSyncApi();
              if (!api || !state || !mp || !bundles.length) return false;
              if (typeof api.bridgeAnchorMediaToMainFlow !== 'function') return false;
              let any = false;
              bundles.forEach(function(bundle) {
                  if (!bundle || bundle.id == null || bundle.id === '') return;
                  const bid = String(bundle.id);
                  const configs = state.bundle && state.bundle.configurations && state.bundle.configurations[bid];
                  if (!configs) return;
                  const anchorIdx = (bundle.items || []).findIndex(function(i) {
                      return String(i && i.id) === String(mp.id);
                  });
                  if (anchorIdx < 0) return;
                  const slot = configs[anchorIdx];
                  if (!slot || !slot.properties) return;
                  if (api.bridgeAnchorMediaToMainFlow({
                      mainProduct: mp,
                      state: state,
                      anchorProps: slot.properties
                  })) any = true;
              });
              return any;
          },
    
          /* Backward-compat aliases */
          shouldAutoApplyBundlePersonalization: function(bundle, mainProduct, s, opts) {
              return this.shouldAutoApplyBundleSync(bundle, mainProduct, s, opts);
          },
          applyBundlePersonalizationToAll: function(bundleId, opts) {
              return this.applyBundleSyncToAll(bundleId, opts);
          },
          maybeAutoApplyBundlePersonalization: function(bundleId) {
              return this.maybeAutoApplyBundleSync(bundleId);
          }
      });
    return true;
  }

  global.PBOfferBundleSyncPatch = patchProductBuilder;

  if (!patchProductBuilder() && global.document) {
    global.document.addEventListener('DOMContentLoaded', patchProductBuilder, { once: true });
  }
})(typeof window !== 'undefined' ? window : globalThis);
